<template>
	<div class="v-data-footer">
		<div class="v-data-footer__select">
			<div class="v-data-footer__select-items">
				{{ t('common.items_per_page') }}
			</div>
			<v-select
				:model-value="itemsPerPage"
				:items="itemsPerPageOptions"
				hide-details="auto"
				variant="outlined"
				density="compact"
				class="v-data-footer__select-input"
				data-test="items-per-page-select"
				@update:model-value="$emit('update:items-per-page', $event)"
			/>
		</div>

		<div class="v-data-footer__pagination">
			<span>{{ pageStart }}-{{ pageStop }} von {{ itemsLength }}</span>
		</div>

		<div class="v-data-footer__icons">
			<v-btn
				:disabled="page === 1"
				icon="fa fa-backward-step"
				variant="plain"
				density="comfortable"
				@click="$emit('update:page', page - 1)"
			/>
			<v-btn
				:disabled="page === 1"
				icon="fa fa-chevron-left"
				variant="plain"
				density="comfortable"
				@click="$emit('update:page', page - 1)"
			/>
			<v-btn
				:disabled="page >= pageCount"
				icon="fa fa-chevron-right"
				variant="plain"
				density="comfortable"
				@click="$emit('update:page', page + 1)"
			/>
			<v-btn
				:disabled="page === 1"
				icon="fa fa-forward-step"
				variant="plain"
				density="comfortable"
				@click="$emit('update:page', page - 1)"
			/>
		</div>
	</div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

interface CardFooterProps {
	page: number
	itemsPerPage: number
	itemsLength: number
	itemsPerPageOptions?: number[]
}

const { t } = useI18n()

const props = withDefaults(defineProps<CardFooterProps>(), {
	page: 1,
	itemsPerPage: 10,
	itemsLength: 0,
	itemsPerPageOptions: () => [10, 25, 50, 100],
})

const emit = defineEmits<{
	'update:page': [page: number]
	'update:items-per-page': [itemsPerPage: number]
}>()

const pageStart = computed(() => {
	if (props.itemsLength === 0) return 0
	return (props.page - 1) * props.itemsPerPage + 1
})

const pageStop = computed(() => {
	if (props.itemsLength === 0) return 0
	const itemsLeft = props.itemsLength - (props.page - 1) * props.itemsPerPage
	return Math.min(
		props.page * props.itemsPerPage,
		(props.page - 1) * props.itemsPerPage + itemsLeft
	)
})

const pageCount = computed(() => {
	return Math.ceil(props.itemsLength / props.itemsPerPage)
})
</script>

<style lang="scss">
.v-data-footer {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  padding: 0.5rem 0.25rem;

  &__select {
    display: flex;
    align-items: center;
    justify-content: center;

    &-items {
      white-space: nowrap;
      padding: 0.5rem;
    }

    &-input {
      width: 100px;
      margin: 0;
    }
  }

  &__pagination {
    display: flex;
    justify-content: flex-end;
    min-width: 116px;
    padding: 0 1rem;
  }

  &__icons {
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
}
</style>
